import AsyncStorage from '@react-native-community/async-storage';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

import { getSupabaseConfig } from './config';

/**
 * Shared Supabase client.
 *
 * Lazy singleton: created once on first access and reused, to avoid opening
 * multiple connections.
 *
 * Auth options:
 * - storage: AsyncStorage — persisted so the PKCE code_verifier survives
 *   process death (the NHS OTP step routinely forces an app switch where
 *   Android may kill the app in the background). A later iteration will
 *   replace this with a session-scoped adapter restricted to verifier keys.
 * - autoRefreshToken: false — the Supabase session is a transient handoff
 *   credential; the Big Health session (OS cookie jar) stays authoritative.
 * - detectSessionInUrl: false — the app handles deep links itself; the SDK
 *   must not try to parse browser URLs.
 */

let client: SupabaseClient | null = null;

export const getSupabaseClient = (): SupabaseClient => {
  if (!client) {
    const { url, publishableKey } = getSupabaseConfig();
    client = createClient(url, publishableKey, {
      auth: {
        storage: AsyncStorage,
        autoRefreshToken: false,
        persistSession: true,
        detectSessionInUrl: false,
      },
    });
  }

  return client;
};

/**
 * Resets the client instance. Used in tests to get a fresh client between
 * test cases (and available for future logout scenarios).
 */
export const resetSupabaseClient = (): void => {
  client = null;
};
